import { Grid, Modal } from '@material-ui/core';
import React, { RefAttributes, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { popupAlerts } from 'src/alerts/business-logic/handle-alerts';
import InteractiveDialogContent from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import InteractiveDialogueContainer from 'src/common/components/interactive-dialogue/components/interactive-dialogue-container';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { SizeType } from 'src/common/enumerations/cashier-preference-level';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import XLRLogger from 'src/common/helpers/logger';
import { CashierPreferences } from 'src/common/interfaces/dtos/CashierPreferences';
import { IDeviceStatusItem } from 'src/common/interfaces/states/IDeviceStatusItem';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { useTranslation } from 'react-i18next';
import { AppState } from 'src/common/redux/configure-store';
import { showDeviceStatusDialog } from 'src/device-status/device-status';
import { getScannerCallback } from 'src/devices/scanner-operations';
import { getCashierAlertTemplate } from 'src/home-page/business-logic/get-alert-template';
import {
  SvgCoupon,
  SvgCouponActive,
  SvgPromotions,
  SvgPromotionSelected,
} from 'src/item-list/components/item-list-icons';
import { lockUser } from 'src/login/business-logic/lock-user';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { popupNotifications } from 'src/notifications/business-logic/notification-actions-handler';
import { searchProduct } from 'src/search/business-logic/handle-search';
import TopAreaButton from 'src/top-area-button/top-area-button/top-area-button';
import { TopAreaMiddleButtonTypes } from '../enums/top-area-button-types';
import { Gift, GiftCardSelected } from '../media';
import { TopAreaSummaryMenu } from './summary-menu/summary-menu';
import TopAreaLayout from './top-area-layout';
import { styles } from './top-area-wrapper-styles';
import ITopAreaButton from '../../common/interfaces/dtos/ITopBarButton';
import { ICommand, IPosUiMenu } from '../../common/interfaces/states/IPosUiMenuState';
import { themeColor } from '../../mui-theme';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { useAuthContext } from 'src/auth/auth-context';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';

type Props = ITopAreaWrapperState;

const TopAreaWrapper: React.FC<Props> = ({
  cashierPreferences,
  isSummaryDisabled,
  items,
  searchLockAndMiddleButtonDisabled,
  device,
  notificationsCount,
  infoCommandMenuConfig,
  popularCommandMenuConfig,
}): JSX.Element => {
  const classes = styles({
    buttonSize: cashierPreferences.buttonZoomLevel,
    textSize: cashierPreferences.textZoomLevel,
  });
  const { t } = useTranslation();
  const [wifiConnection, setWifiConnection] = useState<boolean>(true);
  const [confirmLockout, setLockout] = useState<boolean>(false);
  const [selectedTopBarButton, setSelectedTopBarButton] = useState<TopAreaMiddleButtonTypes>(
    TopAreaMiddleButtonTypes.unset,
  );

  const ejContainsCoupons = items.find(i => i.Type === EJRecordTypes.Coupon && !i.Voided);
  const ejContainsPromotions = items.find(
    i =>
      (i.Type === EJRecordTypes.Promotion || i.Type === EJRecordTypes.RewardAsTender) && !i.Voided,
  );

  const popupAndGetCashierAlert = (): void => {
    getCashierAlertTemplate()
      .then(popupAlerts)
      .catch(popupMessage);
  };

  const { EDGE_IAM } = getMetaData().xlrConfig;
  const { logout } = useAuthContext();

  useEffect(() => {
    window.addEventListener('online', checkWifi);
    window.addEventListener('offline', checkWifi);

    // clean up event every re-render
    return function cleanup() {
      window.removeEventListener('online', checkWifi);
      window.removeEventListener('offline', checkWifi);
    };
  });

  useEffect((): void => {
    XLRLogger.debug({
      message: 'Cashier Accessibility Update',
      source: 'top-area-wrapper.tsx',
      data: cashierPreferences,
    });
  }, [cashierPreferences]);

  const checkWifi = (): void => {
    XLRLogger.debug({
      message: 'Check Wifi',
      source: 'top-area-wrapper.tsx:checkWifi',
      data: {},
    });
    setWifiConnection(navigator.onLine); // TODO: this is known to be erratic according to MDN. https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine
  };

  const {
    ShowPromotionsCommand,
    ShowCouponsCommand,
    ShowGiftCardsCommand,
    unset,
  } = TopAreaMiddleButtonTypes;
  const buttonSize = useSelector(
    (state: AppState): SizeType => state.cashierPreferences.buttonZoomLevel,
  );

  const middleButtonConfigs = infoCommandMenuConfig.commands;

  const clickMiddleButton = (buttonType: TopAreaMiddleButtonTypes): void => {
    if (selectedTopBarButton === unset) {
      pushEmeraldModal(
        <Modal
          onClose={() => {
            popEmeraldModal();
            setSelectedTopBarButton(unset);
          }}
          open={true}
          BackdropProps={{
            invisible: true,
          }}
        >
          <Grid
            container
            className={`${classes.middleButtonMenuContainer} 
              ${
                buttonSize === SizeType.Large
                  ? classes.customerContainerStyle
                  : classes.containerStyle
              }`}
          >
            <Grid item className='fullWidth'>
              <TopAreaSummaryMenu menu={buttonType} />
            </Grid>
          </Grid>
        </Modal>,
        getScannerCallback(),
      );
      setSelectedTopBarButton(buttonType);
    } else if (selectedTopBarButton === buttonType) {
      popEmeraldModal();
      setSelectedTopBarButton(unset);
    }
  };

  type MiddleButtonProps = ITopAreaButton & RefAttributes<HTMLDivElement>;

  const enabledIcon: (buttonType: TopAreaMiddleButtonTypes) => JSX.Element = (
    buttonType,
  ): JSX.Element => {
    switch (buttonType) {
      case TopAreaMiddleButtonTypes.ShowPromotionsCommand:
        return selectedTopBarButton === ShowPromotionsCommand ? (
          <SvgPromotionSelected fill={themeColor.defaultTextColor} className={classes.iconStyle} />
        ) : ejContainsPromotions ? (
          <SvgPromotions fill={themeColor.secondaryMain} className={classes.iconStyle} />
        ) : (
          <SvgPromotionSelected className={classes.iconStyle} />
        );
      case TopAreaMiddleButtonTypes.ShowCouponsCommand:
        return selectedTopBarButton === ShowCouponsCommand ? (
          <SvgCoupon fill={themeColor.defaultTextColor} className={classes.iconStyle} />
        ) : ejContainsCoupons ? (
          <SvgCouponActive fill={themeColor.secondaryMain} className={classes.iconStyle} />
        ) : (
          <SvgCoupon className={classes.iconStyle} />
        );
      case TopAreaMiddleButtonTypes.ShowGiftCardsCommand:
        return selectedTopBarButton === ShowGiftCardsCommand ? (
          <GiftCardSelected className={classes.iconStyle} />
        ) : (
          <Gift className={classes.iconStyle} />
        );
      case TopAreaMiddleButtonTypes.unset:
      default:
        return <React.Fragment />;
    }
  };

  const middleButtonProps = middleButtonConfigs.map<MiddleButtonProps>(config => {
    const buttonType = TopAreaMiddleButtonTypes[config.commandId] || TopAreaMiddleButtonTypes.unset;
    return {
      key: buttonType,
      testId: buttonType,
      selected: selectedTopBarButton === buttonType,
      enabledIcon: enabledIcon(buttonType),
      onClick: () => clickMiddleButton(buttonType),
      isDisabled: searchLockAndMiddleButtonDisabled,
      cashierPreferences: cashierPreferences,
      isMiddle: true,
      buttonType: 'alternate',
    };
  });

  type RightButtonProps = ITopAreaButton & RefAttributes<HTMLDivElement>;

  const mapCommandToRightButtonProps = ({ commandId }: ICommand): Partial<RightButtonProps> => {
    const getShowBadge = (commandId: string): boolean => {
      return (
        {
          DeviceStatusCommand: device.some(d => d.warning),
        }[commandId] || false
      );
    };

    const getIsDisabled = (commandId: string): boolean => {
      return (
        {
          SearchCommand: searchLockAndMiddleButtonDisabled,
          LockTerminalCommand: searchLockAndMiddleButtonDisabled,
        }[commandId] || false
      );
    };

    const getNotificationText = (commandId: string): string => {
      return { ShowNotificationsCommand: notificationsCount }[commandId] || '';
    };

    const getOnClick = (commandId: string): (() => void) => {
      return (
        {
          SearchCommand: searchProduct,
          DeviceStatusCommand: showDeviceStatusDialog,
          ShowNotificationsCommand: popupNotifications,
          ShowAlertsCommand: popupAndGetCashierAlert,
          LockTerminalCommand: () => setLockout(true),
        }[commandId] || (() => {})
      );
    };

    return {
      showBadge: getShowBadge(commandId),
      isDisabled: getIsDisabled(commandId),
      notificationText: getNotificationText(commandId),
      onClick: getOnClick(commandId),
    };
  };

  const [currentStates, setCurrentStates] = useState([
    'ANY',
    wifiConnection ? 'WIFI_ON' : 'WIFI_OFF',
    confirmLockout ? 'UNLOCKED' : 'LOCKED',
  ]);

  useEffect(() => {
    setCurrentStates([
      'ANY',
      wifiConnection ? 'WIFI_ON' : 'WIFI_OFF',
      confirmLockout ? 'UNLOCKED' : 'LOCKED',
    ]);
  }, [wifiConnection, confirmLockout]);

  const byCurrentStateList = (command: ICommand): boolean => {
    const { states } = command;
    return states.some(({ state }) => currentStates.some(currentState => currentState === state));
  };

  const byCommandOrder = (a: ICommand, b: ICommand): number => {
    return +a.commandOrder - +b.commandOrder;
  };

  const enabledForAllCurrentStates = (command: ICommand): boolean => {
    return command.states
      .filter(({ state }) => currentStates.find(cs => cs === state))
      .every(state => state.enabled);
  };

  const rightButtonProps = popularCommandMenuConfig.commands
    .filter(byCurrentStateList)
    .sort(byCommandOrder)
    .map<RightButtonProps>(command => {
      const { commandId, commandIcon } = command;
      return {
        key: commandId,
        testId: commandId,
        icon: commandIcon,
        enabled: enabledForAllCurrentStates(command),
        ...mapCommandToRightButtonProps(command),
      };
    });

  return (
    <>
      <TopAreaLayout
        isSummaryDisabled={isSummaryDisabled}
        middleButtons={middleButtonProps.map(props => (
          <TopAreaButton key={props.key} {...props} />
        ))}
        rightButtons={rightButtonProps.map(props => (
          <TopAreaButton key={props.key} {...props} cashierPreferences={cashierPreferences} />
        ))}
        cashierPreferences={cashierPreferences}
      />

      {confirmLockout && (
        <InteractiveDialogueContainer
          dialogTestIdPrefix='top-area'
          open={true}
          handleClose={() => setLockout(false)}
          handleAccept={async (): Promise<void> => {
            await withSpinner(async () => {
              await voidEmptyTransaction();
              await lockUser();
              if (EDGE_IAM) {
                await logout();
              }
            });
            setLockout(false);
          }}
          component={
            <InteractiveDialogContent
              type={EmeraldDialogTypes.InfoDecision}
              message={'Lock_Caption'}
              subMessage={'Lock_Prompt'}
            />
          }
          acceptButtonLabel={t('Ok')}
        />
      )}
    </>
  );
};

export interface ITopAreaWrapperState {
  canLogout: boolean;
  notificationsCount: string;
  searchLockAndMiddleButtonDisabled: boolean;
  cashierPreferences: CashierPreferences;
  items: IElectronicJournalRecord[];
  isSummaryDisabled: boolean;
  device: IDeviceStatusItem[];
  infoCommandMenuConfig: IPosUiMenu;
  popularCommandMenuConfig: IPosUiMenu;
}

const mapStateToProps = (state: AppState): ITopAreaWrapperState => ({
  canLogout: state.itemList.ejItems.length === 0,
  notificationsCount: state.notification.notifications.length.toString(),
  searchLockAndMiddleButtonDisabled: state.baseView.searchLockAndMiddleButtonDisabled,
  items: state.itemList.ejItems,
  cashierPreferences: state.cashierPreferences,
  isSummaryDisabled: state.tendering.isTendering,
  device: state.deviceStatus.devices,
  infoCommandMenuConfig: state.posUiMenu.Info,
  popularCommandMenuConfig: state.posUiMenu.Popular,
});

export default connect(mapStateToProps)(TopAreaWrapper);
